import React, { useCallback, useEffect, useRef, useState } from "react";

import { cybervolunteerApi } from "../../api/cybervolunteerApi";
import dragHandleIcon from "../../assets/img/icons/dragHandleIcon.svg";
import Header from "../../components/Header";
import type { SubtaskType } from "./AdminTaskListPageTypes";

export function reorderRows(
  items: Array<SubtaskType>,
  draggedId: number,
  targetId: number
): Array<SubtaskType> {
  const fromIdx = items.findIndex((t) => t.id === draggedId);
  const toIdx = items.findIndex((t) => t.id === targetId);
  if (fromIdx < 0 || toIdx < 0 || fromIdx === toIdx) {
    return items;
  }
  const next = [...items];
  const [removed] = next.splice(fromIdx, 1);
  next.splice(toIdx, 0, removed);
  return next;
}

const AdminTaskListPage = () => {
  const [taskList, setTaskList] = useState<Array<SubtaskType>>([]);
  const taskListRef = useRef<Array<SubtaskType>>([]);
  useEffect(() => {
    taskListRef.current = taskList;
  }, [taskList]);
  const [draggingTaskId, setDraggingTaskId] = useState<number | null>(null);

  const change_status = (subtask_id: number, subtask_status: string) => {
    cybervolunteerApi
      .updateSubtask({
        subtask_id: subtask_id,
        subtask_status: subtask_status,
      })
      .then((_response) => {
        setTaskList((prev) =>
          prev.map((subtask) =>
            subtask.id === subtask_id ? { ...subtask, status: subtask_status } : subtask
          )
        );
      })
      .catch((error) => {
        console.error("There was an error fetching the data!", error);
        if (error.response.status === 405) {
          window.location.href = "/";
        }
      });
  };
  const get_date = (date_time: string) => {
    let date_1 = new Date(date_time);
    const warsawDate = new Date(date_1.toLocaleString("en-US", { timeZone: "Europe/Warsaw" }));

    const year = warsawDate.getFullYear();
    const month = String(warsawDate.getMonth() + 1).padStart(2, "0");
    const day = String(warsawDate.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const get_csv = (task_number: number, task_title: string) => {
    cybervolunteerApi.getCsvBlob(task_number).then((response) => {
      const href = URL.createObjectURL(response.data);

      const link = document.createElement("a");
      link.href = href;
      link.setAttribute("download", task_title + ".csv");
      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);
      URL.revokeObjectURL(href);
    });
  };

  const is_admin = () => {
    cybervolunteerApi
      .checkAdmin()
      .then((_response) => {})
      .catch((error) => {
        console.error("There was an error fetching the data!", error);
        if (error.response.status === 405) {
          window.location.href = "/";
        }
      });
  };

  const get_task_data = useCallback(() => {
    cybervolunteerApi
      .getAdminSubtasks()
      .then((response) => {
        setTaskList(response.data);
      })
      .catch((error) => {
        console.error("There was an error fetching the data!", error);
        if (error.response.status === 405) {
          window.location.href = "/";
        }
      });
  }, []);

  const persistTaskOrder = useCallback(
    async (ordered: Array<SubtaskType>) => {
      const ids = ordered.map((t) => t.id);
      try {
        await cybervolunteerApi.reorderSubtasks({ ordered_ids: ids });
      } catch (error: unknown) {
        console.error("There was an error reordering tasks!", error);
        const status =
          error &&
          typeof error === "object" &&
          "response" in error &&
          typeof (error as { response?: { status?: unknown } }).response?.status === "number"
            ? (error as { response: { status: number } }).response.status
            : undefined;
        if (status === 405) {
          window.location.href = "/";
          return;
        }
        get_task_data();
        return;
      }
      setTaskList(ordered.map((t, index) => ({ ...t, order: index })));
    },
    [get_task_data]
  );

  const handleDropRow = useCallback(
    (e: React.DragEvent<HTMLTableRowElement>, targetRowId: number) => {
      e.preventDefault();
      const raw = e.dataTransfer.getData("text/plain");
      const draggedId = Number.parseInt(raw, 10);
      setDraggingTaskId(null);
      if (
        !Number.isFinite(draggedId) ||
        draggedId === targetRowId ||
        draggedId <= 0 ||
        targetRowId <= 0
      ) {
        return;
      }
      const prev = taskListRef.current;
      const next = reorderRows(prev, draggedId, targetRowId);
      const unchanged =
        next === prev ||
        (next.length === prev.length && next.every((t, i) => t.id === prev[i]?.id));
      if (unchanged) {
        return;
      }
      taskListRef.current = next;
      setTaskList(next);
      void persistTaskOrder(next);
    },
    [persistTaskOrder]
  );

  useEffect(() => {
    is_admin();
  }, []);

  useEffect(() => {
    get_task_data();
  }, [get_task_data]);

  return (
    <div className="main-body border-l-orange-100 md:pb-10">
      <Header />
      <main className="w-full">
        <div className="max-w-screen-xl mx-auto">
          <div className="mt-3 max-w-screen-xl mx-auto shadow-[0_0_10px_rgba(0,0,0,0.25)] rounded-md p-3 text-black flex flex-col">
            <button
              type="button"
              onClick={() => {
                window.location.href = "/create_task";
              }}
              className="w-full hover:bg-inherit shadow-[0_0_3px_rgba(0,0,0,0.25)] text-lg p-2 mb-3 rounded-md"
            >
              Создание задачи
            </button>
            <table className="shadow-sm rounded-md p-3 w-full border-collapse">
              <thead className="py-3 rounded-lg">
                <tr>
                  <th className="py-3 px-1 w-[2rem]" aria-label="" />
                  <th className="py-3 px-2 text-left">Дата, название</th>
                  <th className="py-3 px-2">Hero</th>
                  <th className="py-3 px-2">Редактирование</th>
                  <th className="py-3 px-2">Количество</th>
                  <th className="py-3 px-2">Скачать в CSV</th>
                  <th className="py-3 px-2">Статус</th>
                </tr>
              </thead>
              <tbody
                onDragOver={(e) => {
                  e.preventDefault();
                  e.dataTransfer.dropEffect = "move";
                }}
              >
                {taskList.map((task1) => (
                  <tr
                    key={task1.id}
                    className={draggingTaskId === task1.id ? "opacity-50" : ""}
                    onDragOver={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      e.dataTransfer.dropEffect = "move";
                    }}
                    onDrop={(e) => handleDropRow(e, task1.id)}
                    onDragEnd={() => setDraggingTaskId(null)}
                  >
                    <td className="py-2 px-1 align-middle w-[2rem]">
                      <span
                        role="presentation"
                        tabIndex={-1}
                        draggable={task1.id > 0}
                        onDragStart={(e) => {
                          if (task1.id <= 0) {
                            e.preventDefault();
                            return;
                          }
                          e.dataTransfer.setData("text/plain", String(task1.id));
                          e.dataTransfer.effectAllowed = "move";
                          setDraggingTaskId(task1.id);
                        }}
                        title="Тащите, чтобы изменить порядок"
                        className="inline-flex cursor-grab active:cursor-grabbing touch-none rounded p-1 text-neutral-600 hover:bg-neutral-200 select-none justify-center align-middle leading-none border-0 bg-transparent appearance-none mx-auto"
                      >
                        <img
                          src={dragHandleIcon}
                          width={18}
                          height={14}
                          alt=""
                          aria-hidden
                          className="block"
                        />
                      </span>
                    </td>
                    <td>
                      {get_date(task1.start_date)} : {task1.title}
                    </td>
                    <td className="px-2 text-center">{task1.is_hero ? "да" : "нет"}</td>
                    <td>
                      <button
                        type="button"
                        onClick={(_e) =>
                          (window.location.href = `/edit_task/${task1.id.toString()}`)
                        }
                        className="w-full border-2 basis-64 rounded-lg p-2"
                      >
                        Edit
                      </button>
                    </td>
                    <td className="w-auto px-2 text-center">{task1.amount}</td>
                    <td className="p-2">
                      <button
                        type="button"
                        onClick={() => get_csv(task1.id, task1.title)}
                        className="w-full border-2 basis-64 rounded-lg p-2"
                      >
                        {"CSV"}
                      </button>
                    </td>
                    <td className="p-2">
                      <button
                        type="button"
                        aria-label={`Статус задачи ${task1.id}: ${task1.status}`}
                        onClick={(_e) => {
                          change_status(task1.id, task1.status === "open" ? "close" : "open");
                        }}
                        className="border-2 basis-64 w-full rounded-lg p-2"
                      >
                        {task1.status === "open" ? task1.status : task1.status}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AdminTaskListPage;
